import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteContent } from "@/lib/siteContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, XCircle, Copy, ExternalLink, Save, FileText, Eye, Mail, MessageCircle, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

function Stat({ label, value }) {
  return (
    <div className="section-card">
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-3xl text-[#E8ECF3] mt-2">{value}</div>
    </div>
  );
}

const CONTENT_FIELDS = [
  { key: "hero_eyebrow", label: "Hero eyebrow (small text above heading)", input: "input" },
  { key: "hero_headline", label: "Hero headline (main)", input: "input" },
  { key: "hero_headline_italic", label: "Hero headline (italic teal part)", input: "input" },
  { key: "hero_sub", label: "Hero sub-text", input: "textarea", rows: 3 },
  { key: "hero_cta_primary", label: "Primary CTA button text", input: "input" },
  { key: "hero_cta_secondary", label: "Secondary CTA button text", input: "input" },
  { key: "founder_name", label: "Founder name", input: "input" },
  { key: "founder_age", label: "Founder age", input: "input" },
  { key: "founder_role", label: "Founder role/title", input: "input" },
  { key: "story_title", label: "About page — title", input: "input" },
  { key: "story_body", label: "About page — full story (use blank line between paragraphs)", input: "textarea", rows: 14 },
  { key: "why_choose_title", label: "'Why Choose' section title", input: "input" },
  { key: "process_title", label: "'Process' section title", input: "input" },
  { key: "cta_final_title", label: "Final CTA title (bottom of homepage)", input: "input" },
  { key: "cta_final_sub", label: "Final CTA sub-text", input: "textarea", rows: 2 },
];

function ContentEditor() {
  const { content, refresh } = useSiteContent();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (content && Object.keys(form).length === 0) {
      setForm(content);
    }
    // eslint-disable-next-line
  }, [content]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      CONTENT_FIELDS.forEach((f) => { if (form[f.key] !== undefined) payload[f.key] = form[f.key]; });
      await api.put("/site-content", payload);
      await refresh();
      toast.success("Content saved. Refresh any page to see updates.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="section-card">
        <div className="label-eyebrow mb-2">How this works</div>
        <p className="text-sm text-[#94A3B8]">
          Change any text below and click <strong className="text-[#E8ECF3]">Save Changes</strong>. Updates go live immediately on the website — no code change needed. Refresh the homepage in a new tab to see them.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {CONTENT_FIELDS.map((f) => (
          <div key={f.key} className="section-card">
            <Label className="text-[#94A3B8]">{f.label}</Label>
            {f.input === "textarea" ? (
              <Textarea rows={f.rows || 3} className="mt-2 bg-[#0A1223] border-[#142542] text-[#E8ECF3]"
                        value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                        data-testid={`content-${f.key}`} />
            ) : (
              <Input className="mt-2 h-11 bg-[#0A1223] border-[#142542] text-[#E8ECF3]"
                     value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                     data-testid={`content-${f.key}`} />
            )}
          </div>
        ))}
      </div>

      <div className="sticky bottom-6 flex justify-end">
        <button onClick={save} disabled={busy} className="pill-solid h-12 px-8 font-medium flex items-center gap-2" data-testid="save-content">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordDialog() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async () => {
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    if (newPw.length < 6) { toast.error("Min 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: oldPw, new_password: newPw });
      toast.success("Password changed! Use it next time you log in.");
      setOldPw(""); setNewPw(""); setConfirmPw(""); setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="pill-glow px-6 h-11 text-sm inline-flex items-center gap-2" data-testid="open-change-password">
          <KeyRound className="h-4 w-4" /> Change Password
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-[#0F1B33] border-[#142542] text-[#E8ECF3]">
        <DialogHeader><DialogTitle className="font-display text-2xl">Change your admin password</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[#94A3B8]">Current password</Label>
            <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)}
                   className="mt-2 h-11 bg-[#0A1223] border-[#142542] text-[#E8ECF3]" data-testid="cp-current" />
          </div>
          <div>
            <Label className="text-[#94A3B8]">New password</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                   className="mt-2 h-11 bg-[#0A1223] border-[#142542] text-[#E8ECF3]" data-testid="cp-new" />
          </div>
          <div>
            <Label className="text-[#94A3B8]">Confirm new password</Label>
            <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                   className="mt-2 h-11 bg-[#0A1223] border-[#142542] text-[#E8ECF3]" data-testid="cp-confirm" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={busy} className="pill-solid h-11 px-6 font-medium w-full" data-testid="cp-submit">
            {busy ? "Saving…" : "Change password"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPage() {
  const [pending, setPending] = useState([]);
  const [allPros, setAllPros] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [userDocs, setUserDocs] = useState({}); // {user_id: [docs]}

  const load = async () => {
    try {
      const [p, s, ap, ab, au, pc, nf] = await Promise.all([
        api.get("/admin/pending-verifications"),
        api.get("/admin/stats"),
        api.get("/admin/all-professionals"),
        api.get("/admin/all-bookings"),
        api.get("/admin/all-users"),
        api.get("/payments/config"),
        api.get("/admin/notifications"),
      ]);
      setPending(p.data); setStats(s.data);
      setAllPros(ap.data); setBookings(ab.data); setUsers(au.data);
      setPaymentEnabled(pc.data.enabled && pc.data.key_id?.startsWith("rzp_live"));
      setNotifs(nf.data);
      // Fetch docs for each pending user
      const docsMap = {};
      await Promise.all(p.data.map(async (pro) => {
        try {
          const { data } = await api.get(`/admin/user-docs/${pro.user_id}`);
          docsMap[pro.user_id] = data;
        } catch { docsMap[pro.user_id] = []; }
      }));
      setUserDocs(docsMap);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, approved) => {
    await api.post(`/admin/verify/${id}`, { approved });
    toast.success(approved ? "Approved" : "Rejected");
    load();
  };

  const copyEmail = (e) => { navigator.clipboard.writeText(e); toast.success("Email copied"); };

  const sendOne = async (notifId) => {
    try {
      const { data } = await api.post(`/admin/notifications/${notifId}/send`);
      if (data.ok) toast.success("Sent!");
      else toast.error(data.error || "Send failed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendAllPending = async () => {
    try {
      const { data } = await api.post("/admin/notifications/send-pending");
      toast.success(`Sent ${data.sent}, failed ${data.failed}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const totalRevenue = bookings.filter((b) => b.payment_status?.startsWith("paid")).reduce((a, b) => a + (b.commission || 0), 0);

  return (
    <div className="bg-[#05070C] min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">Control Center</div>
            <h1 className="font-display text-4xl md:text-5xl text-[#E8ECF3] mt-3">Admin Dashboard</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={load} className="pill-glow px-6 h-11 text-sm" data-testid="admin-refresh">Refresh</button>
            <ChangePasswordDialog />
          </div>
        </div>

        {paymentEnabled && (
          <div className="mb-8 rounded-2xl border border-[#2DD4BF]/40 bg-[#2DD4BF]/5 p-4 flex items-start gap-3">
            <div className="text-[#2DD4BF] font-display text-2xl leading-none">!</div>
            <div className="text-sm text-[#CBD5E1]">
              <strong className="text-[#2DD4BF]">Razorpay LIVE mode is active.</strong> Real money will be charged on every booking. Refunds must be issued from Razorpay dashboard.
            </div>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10">
            <Stat label="Users" value={stats.users} />
            <Stat label="Experts" value={stats.professionals} />
            <Stat label="Verified" value={stats.verified} />
            <Stat label="Bookings" value={stats.bookings} />
            <Stat label="Reviews" value={stats.reviews} />
            <Stat label="Revenue ₹" value={totalRevenue.toLocaleString("en-IN")} />
          </div>
        )}

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="flex flex-wrap gap-2 max-w-3xl mb-6 bg-[#0F1B33] p-1">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending {pending.length > 0 && <span className="ml-2 bg-[#2DD4BF] text-[#05070C] text-xs px-2 py-0.5 rounded-full">{pending.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="experts" data-testid="tab-experts">All Experts ({allPros.length})</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              Messages {notifs.filter((n) => n.status === "pending").length > 0 && (
                <span className="ml-2 bg-[#2DD4BF] text-[#05070C] text-xs px-2 py-0.5 rounded-full">{notifs.filter((n) => n.status === "pending").length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="text-[#2DD4BF]">✎ Content Editor</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {pending.length === 0 ? (
              <div className="border border-dashed border-[#142542] rounded-3xl p-16 text-center text-[#94A3B8]">
                Nothing pending. All caught up!
              </div>
            ) : (
              <div className="space-y-4">
                {pending.map((p) => (
                  <div key={p.user_id} className="section-card" data-testid={`pending-${p.user_id}`}>
                    <div className="flex flex-wrap gap-5 items-start">
                      <img src={p.picture || "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&h=200&w=200"} alt="" className="w-16 h-16 rounded-full object-cover border border-[#142542]" />
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-display text-xl text-[#E8ECF3]">{p.name}</div>
                          <button onClick={() => copyEmail(p.email)} className="text-xs text-[#94A3B8] hover:text-[#2DD4BF] flex items-center gap-1">
                            {p.email} <Copy className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="text-xs text-[#94A3B8] mt-1">{p.headline} · {p.years_experience} yrs · ₹{p.hourly_rate}/hr · {p.location}</div>
                        <div className="text-sm text-[#CBD5E1] mt-3">{p.bio}</div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(p.expertise || []).map((e) => (
                            <span key={e} className="text-xs bg-[#0A1223] border border-[#142542] text-[#94A3B8] rounded-full px-3 py-1">{e}</span>
                          ))}
                        </div>
                        {(userDocs[p.user_id]?.length > 0) && (
                          <div className="mt-4 border-t border-[#142542] pt-3">
                            <div className="label-eyebrow mb-2 flex items-center gap-1"><FileText className="h-3 w-3" /> Documents ({userDocs[p.user_id].length})</div>
                            <div className="flex flex-wrap gap-2">
                              {userDocs[p.user_id].map((d) => (
                                <a key={d.doc_id}
                                   href={`${API}/uploads/${d.doc_id}/view`}
                                   target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-2 bg-[#2DD4BF]/10 border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/20 rounded-full px-3 py-1 text-xs"
                                   data-testid={`admin-doc-${d.doc_id}`}>
                                  <Eye className="h-3 w-3" /> {d.original_filename}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {(!userDocs[p.user_id] || userDocs[p.user_id].length === 0) && (
                          <div className="mt-4 text-xs text-[#94A3B8] italic">No verification documents uploaded yet.</div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        <Link to={`/professionals/${p.user_id}`} target="_blank">
                          <button className="w-full h-9 rounded-full border border-[#142542] text-[#94A3B8] text-sm hover:text-[#E8ECF3] flex items-center justify-center gap-1" data-testid={`view-${p.user_id}`}>
                            <ExternalLink className="h-3 w-3" /> Preview
                          </button>
                        </Link>
                        <button onClick={() => decide(p.user_id, true)} className="pill-solid h-9 text-sm flex items-center justify-center gap-1" data-testid={`approve-${p.user_id}`}>
                          <ShieldCheck className="h-4 w-4" /> Approve
                        </button>
                        <button onClick={() => decide(p.user_id, false)} className="w-full h-9 rounded-full border border-[#142542] text-[#94A3B8] text-sm hover:text-[#E8ECF3] flex items-center justify-center gap-1" data-testid={`reject-${p.user_id}`}>
                          <XCircle className="h-4 w-4" /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="experts">
            <div className="overflow-x-auto section-card p-0">
              <table className="w-full text-sm">
                <thead className="bg-[#0A1223] text-[#94A3B8]">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Category</th><th className="text-left p-3">Rate</th><th className="text-left p-3">Rating</th><th className="text-left p-3">Status</th><th className="text-left p-3">Action</th></tr>
                </thead>
                <tbody className="text-[#E8ECF3]">
                  {allPros.map((p) => (
                    <tr key={p.user_id} className="border-t border-[#142542]">
                      <td className="p-3">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-[#94A3B8]">{p.email}</div>
                      </td>
                      <td className="p-3 capitalize">{p.category}</td>
                      <td className="p-3 text-[#2DD4BF]">₹{p.hourly_rate}</td>
                      <td className="p-3">{p.avg_rating || "—"} ({p.reviews_count || 0})</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${p.verified ? "bg-[#2DD4BF]/20 border border-[#2DD4BF]/40 text-[#2DD4BF]" : "bg-[#142542] text-[#94A3B8]"}`}>
                          {p.verification_status || (p.verified ? "approved" : "pending")}
                        </span>
                      </td>
                      <td className="p-3">
                        {!p.verified ? (
                          <button className="text-xs text-[#2DD4BF] hover:underline" onClick={() => decide(p.user_id, true)} data-testid={`quick-approve-${p.user_id}`}>Approve</button>
                        ) : (
                          <button className="text-xs text-[#94A3B8] hover:text-red-400" onClick={() => decide(p.user_id, false)} data-testid={`quick-unverify-${p.user_id}`}>Unverify</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <div className="overflow-x-auto section-card p-0">
              <table className="w-full text-sm">
                <thead className="bg-[#0A1223] text-[#94A3B8]">
                  <tr><th className="text-left p-3">When</th><th className="text-left p-3">Client → Expert</th><th className="text-left p-3">Type</th><th className="text-left p-3">Total</th><th className="text-left p-3">Commission</th><th className="text-left p-3">Status</th></tr>
                </thead>
                <tbody className="text-[#E8ECF3]">
                  {bookings.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-[#94A3B8]">No bookings yet</td></tr>
                  )}
                  {bookings.map((b) => (
                    <tr key={b.booking_id} className="border-t border-[#142542]">
                      <td className="p-3 text-xs">{new Date(b.scheduled_at).toLocaleString()}</td>
                      <td className="p-3">{b.client_name} → <span className="font-medium">{b.professional_name}</span></td>
                      <td className="p-3 capitalize">{b.session_type}</td>
                      <td className="p-3">₹{b.total_amount?.toLocaleString("en-IN")}</td>
                      <td className="p-3 text-[#2DD4BF]">₹{b.commission?.toLocaleString("en-IN")}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          b.status === "completed" ? "bg-[#2DD4BF]/20 border border-[#2DD4BF]/40 text-[#2DD4BF]" :
                          b.status === "confirmed" ? "bg-[#142542] text-[#E8ECF3]" :
                          b.status === "pending_payment" ? "bg-yellow-500/20 border border-yellow-500/40 text-yellow-400" : "bg-[#0A1223]"
                        }`}>{b.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="overflow-x-auto section-card p-0">
              <table className="w-full text-sm">
                <thead className="bg-[#0A1223] text-[#94A3B8]">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Joined</th></tr>
                </thead>
                <tbody className="text-[#E8ECF3]">
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-[#142542]">
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3">
                        <button onClick={() => copyEmail(u.email)} className="hover:text-[#2DD4BF] flex items-center gap-1">
                          {u.email} <Copy className="h-3 w-3" />
                        </button>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          u.role === "admin" ? "bg-[#2DD4BF] text-[#05070C]" :
                          u.role === "professional" ? "bg-[#2DD4BF]/20 border border-[#2DD4BF]/40 text-[#2DD4BF]" : "bg-[#142542] text-[#94A3B8]"
                        }`}>{u.role}</span>
                      </td>
                      <td className="p-3 text-xs text-[#94A3B8]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="content">
            <ContentEditor />
          </TabsContent>

          <TabsContent value="notifications">
            <div className="section-card mb-4 flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="label-eyebrow mb-2">Welcome messages queue</div>
                <p className="text-sm text-[#94A3B8]">
                  WhatsApp <strong className="text-[#2DD4BF]">LIVE via Twilio</strong> ✓ · Email delivery needs Resend key.
                  Naye user register karne pe WhatsApp auto-send hota hai. Pending ko manually bhi bhej sakte hain.
                </p>
                <p className="text-xs text-[#94A3B8] mt-2">
                  ⚠️ Twilio Sandbox me recipient ko pehle <code className="bg-[#0A1223] px-1 rounded">"join &lt;code&gt;"</code> message send karna padta hai +14155238886 par (dashboard.twilio.com me code milega). Production ke liye WhatsApp Business number approval chahiye.
                </p>
              </div>
              <button onClick={sendAllPending} className="pill-solid px-6 h-11 text-sm font-medium" data-testid="send-all-pending">
                Send All Pending
              </button>
            </div>
            {notifs.length === 0 ? (
              <div className="border border-dashed border-[#142542] rounded-3xl p-12 text-center text-[#94A3B8]">No messages yet.</div>
            ) : (
              <div className="space-y-3">
                {notifs.map((n) => (
                  <div key={n.notif_id} className="section-card" data-testid={`notif-${n.notif_id}`}>
                    <div className="flex items-start gap-4">
                      <div className="teal-icon-circle" style={{ width: 40, height: 40 }}>
                        {n.channel === "email" ? <Mail className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-medium text-[#E8ECF3] capitalize">{n.channel}</div>
                          <div className="text-xs text-[#94A3B8]">{n.to}</div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            n.status === "sent" ? "bg-[#2DD4BF]/20 text-[#2DD4BF] border border-[#2DD4BF]/40" :
                            n.status === "failed" ? "bg-red-500/20 text-red-400 border border-red-500/40" :
                            "bg-[#142542] text-[#94A3B8]"
                          }`}>{n.status}</span>
                          <span className="text-xs text-[#64748B]">{new Date(n.created_at).toLocaleString()}</span>
                        </div>
                        {n.subject && <div className="mt-2 font-medium text-[#E8ECF3]">{n.subject}</div>}
                        <pre className="text-sm text-[#CBD5E1] mt-2 whitespace-pre-wrap font-body">{n.body}</pre>
                        {n.last_error && <div className="mt-2 text-xs text-red-400">Error: {n.last_error}</div>}
                      </div>
                      <div className="flex flex-col gap-2">
                        {n.status === "pending" && n.channel !== "email" && (
                          <button onClick={() => sendOne(n.notif_id)} className="pill-solid px-4 h-9 text-xs font-medium" data-testid={`send-notif-${n.notif_id}`}>
                            Send
                          </button>
                        )}
                        <button onClick={() => { navigator.clipboard.writeText(n.body); toast.success("Copied"); }}
                                className="text-[#2DD4BF] text-xs hover:underline" data-testid={`copy-notif-${n.notif_id}`}>
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
